import {
  Icon,
  RouteButtonLink,
} from "@equinor/e-procurement-portal-and-ts-portal-front-end-components";
import { mdiBellOutline, mdiClose } from "@mdi/js";
import {
  Badge,
  Box,
  Button,
  CircularProgress,
  ClickAwayListener,
  Divider,
  Grow,
  IconButton,
  Link,
  List,
  ListItem,
  ListItemButton,
  ListSubheader,
  Paper,
  Popper,
  Tooltip,
  Typography,
} from "@mui/material";
import type { Theme } from "@mui/material/styles";
import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { Link as LinkRoute } from "@tanstack/react-router";
import {
  useCallback,
  useEffect,
  useEffectEvent,
  useId,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useInView } from "react-intersection-observer";
import type {
  InfiniteNotificationsData,
  MercureNotificationsResponse,
  Notification,
} from "../../../@types/services/Notification";
import { useAuth } from "../../../hooks/useAuth";
import { subscribeUserMessageChannel } from "../../../services/mercure";
import {
  fetchNotifications,
  markAsReadNotification,
  markAsSeenNotification,
} from "../../../services/notifications";
import { i18nextParseToDate } from "../../../utils/i18nextParseTo";

export const Notifications: React.FC = () => {
  const { user } = useAuth();
  const userId = user?.id;
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const [open, setOpen] = useState<boolean>(false);
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const anchorRef = useRef(null);
  const { ref, inView } = useInView({
    threshold: 0,
  });

  const {
    data: notificationsData,
    isLoading,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    // biome-ignore assist/source/useSortedKeys: ordering required for type inference, waiting a TanStack Biome plugin
  } = useInfiniteQuery({
    queryKey: ["notifications", "list", user?.id],
    initialPageParam: 1,
    queryFn: ({ pageParam }) =>
      fetchNotifications({
        filters: { notifications_read: false },
        items_per_page: 10,
        page: pageParam,
      }),
    getNextPageParam: (lastPage) => {
      const nextPage = lastPage.pagination.current_page + 1;
      return nextPage <= lastPage.pagination.total_pages ? nextPage : undefined;
    },
    select: (data) => ({
      notifications: data.pages.flatMap((page) => page.results || []),
      notificationsCount: data.pages[0]?.total_items ?? 0,
    }),
    enabled: !!user,
  });

  const { mutate: markAsRead } = useMutation({
    mutationFn: markAsReadNotification,
    onSuccess: (updatedNotification: Notification) => {
      queryClient.setQueryData(
        ["notifications", "list", user?.id],
        (oldData?: InfiniteNotificationsData) => {
          if (!oldData?.pages) return oldData;

          const wasUnread = oldData.pages.some((page) =>
            page.results?.some(
              (notification) =>
                notification.id === updatedNotification.id &&
                !notification.read_at,
            ),
          );

          if (wasUnread) {
            const updatedPages = oldData.pages.map((page) => ({
              ...page,
              results:
                page.results?.filter(
                  (notification: Notification) =>
                    notification.id !== updatedNotification.id,
                ) || [],
              total_items: page.total_items - 1,
            }));

            const nonEmptyPages = updatedPages.filter(
              (page) => page.results && page.results.length > 0,
            );
            const finalPages =
              nonEmptyPages.length > 0 ? nonEmptyPages : [updatedPages[0]];

            return {
              ...oldData,
              pageParams: finalPages.map((_, index) => index + 1),
              pages: finalPages,
            };
          }

          const updatedPages = oldData.pages.map((page) => ({
            ...page,
            results:
              page.results?.filter(
                (notification: Notification) =>
                  notification.id !== updatedNotification.id,
              ) || [],
          }));

          const nonEmptyPages = updatedPages.filter(
            (page) => page.results && page.results.length > 0,
          );
          const finalPages =
            nonEmptyPages.length > 0 ? nonEmptyPages : [updatedPages[0]];

          return {
            ...oldData,
            pageParams: finalPages.map((_, index) => index + 1),
            pages: finalPages,
          };
        },
      );
    },
  });

  const { mutate: markAsSeen } = useMutation({
    mutationFn: markAsSeenNotification,
    onSuccess: (updatedNotification: Notification) => {
      queryClient.setQueryData(
        ["notifications", "list", user?.id],
        (oldData?: InfiniteNotificationsData) => {
          if (!oldData?.pages) return oldData;

          return {
            ...oldData,
            pages: oldData.pages.map((page) => ({
              ...page,
              results: page.results?.map((notification: Notification) =>
                notification.id === updatedNotification.id
                  ? updatedNotification
                  : notification,
              ),
            })),
          };
        },
      );
    },
  });

  const handleToggle = useCallback(() => {
    setOpen((prevOpen) => !prevOpen);
    setTooltipOpen(false);
  }, []);

  const handleTooltipClose = useCallback(() => setTooltipOpen(false), []);
  const handleTooltipOpen = useCallback(() => setTooltipOpen(!open), [open]);
  const handleClickAway = useCallback(() => setOpen(false), []);
  const handleNotificationMouseEnter = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      const notification = notificationsData?.notifications.find(
        (item) =>
          String(item.id) === event.currentTarget.dataset.notificationId,
      );

      if (notification && !notification.seen_at) {
        markAsSeen(notification.id);
      }
    },
    [markAsSeen, notificationsData?.notifications],
  );
  const handleMarkAsRead = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      event.preventDefault();
      event.stopPropagation();
      const notification = notificationsData?.notifications.find(
        (item) =>
          String(item.id) === event.currentTarget.dataset.notificationId,
      );

      if (notification) {
        markAsRead(notification.id);
      }
    },
    [markAsRead, notificationsData?.notifications],
  );
  const paperSx = useCallback(
    (theme: Theme) => ({
      backgroundImage: "none",
      border: "1px solid",
      borderColor: "grey.200",
      boxShadow: `0px 4px 20px rgba(170, 180, 190, 0.3)`,
      mt: 0.5,
      transformOrigin: "top right",
      ...theme.applyStyles("dark", {
        borderColor: "primaryDark.700",
        boxShadow: `0px 4px 20px rgba(0, 0, 0, 0.5)`,
      }),
    }),
    [],
  );
  const popupContentSx = useCallback(
    (theme: Theme) => ({
      maxHeight: 540,
      overflow: "auto",
      width: theme.spacing(50),
    }),
    [],
  );
  const loadingSx = useCallback(
    (theme: Theme) => ({
      alignItems: "center",
      display: "flex",
      justifyContent: "center",
      margin: theme.spacing(3, 0),
    }),
    [],
  );
  const listHeaderSx = useCallback(
    (theme: Theme) => ({
      alignItems: "center",
      display: "flex",
      justifyContent: "space-between",
      padding: theme.spacing(1, 2),
      width: "100%",
    }),
    [],
  );
  const dividerSx = useCallback(
    (theme: Theme) => ({ margin: theme.spacing(1, 0) }),
    [],
  );

  const handleMessageCallback = useCallback(
    (data: string) => {
      const parsedData = JSON.parse(data) as MercureNotificationsResponse;

      if (
        parsedData.notification_type === "ChatMessageNotifier::Notification"
      ) {
        void queryClient.invalidateQueries({ queryKey: ["notifications"] });
        return;
      }

      queryClient.setQueryData(
        ["notifications", "list", user?.id],
        (oldData?: InfiniteNotificationsData) => {
          if (!oldData?.pages) {
            return {
              pageParams: [1],
              pages: [
                {
                  results: [parsedData.notification],
                  total_items: 1,
                },
              ],
            };
          }

          const notificationExists = oldData.pages.some((page) =>
            page.results?.some(
              (notification) => notification.id === parsedData.notification.id,
            ),
          );

          if (notificationExists) {
            return oldData;
          }

          const updatedPages = oldData.pages.map((page, index) =>
            index === 0
              ? {
                  ...page,
                  results: [parsedData.notification, ...(page.results || [])],
                  total_items: page.total_items + 1,
                }
              : {
                  ...page,
                  total_items: page.total_items + 1,
                },
          );

          return {
            ...oldData,
            pages: updatedPages,
          };
        },
      );
    },
    [queryClient, user],
  );

  const onMessage = useEffectEvent((data: string) => {
    handleMessageCallback(data);
  });

  useEffect(() => {
    // Prevent search engines from indexing the notification.
    if (/glebot/.test(navigator.userAgent) || !userId) {
      return;
    }

    return subscribeUserMessageChannel(
      { id: userId },
      "notifications",
      (data) => onMessage(data),
    );
  }, [userId]);

  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [inView, fetchNextPage, hasNextPage, isFetchingNextPage]);

  const notificationsPopupId = useId();

  return (
    <>
      <Tooltip
        arrow
        enterDelay={300}
        onClose={handleTooltipClose}
        onOpen={handleTooltipOpen}
        open={tooltipOpen}
        title={t("notifications.toggle")}
      >
        <IconButton
          aria-controls={open ? notificationsPopupId : undefined}
          aria-haspopup="true"
          aria-label={`Notifications ${notificationsData?.notificationsCount ?? 0} ${t("notifications.unread")}`}
          color="slate"
          data-ga-event-action="toggleNotifications"
          data-ga-event-category="AppBar"
          onClick={handleToggle}
          ref={anchorRef}
          sx={{ gridArea: "notifications", ml: 1 }}
        >
          <Badge
            badgeContent={notificationsData?.notificationsCount}
            color="energyRed"
            max={99}
          >
            <Icon icon={mdiBellOutline} />
          </Badge>
        </IconButton>
      </Tooltip>
      <Popper
        anchorEl={anchorRef.current}
        data-testid="notifications-popper"
        disablePortal
        id={notificationsPopupId}
        open={open}
        placement="bottom-end"
        transition
      >
        {({ TransitionProps }) => (
          <ClickAwayListener onClickAway={handleClickAway}>
            <Grow
              in={TransitionProps?.in}
              onEnter={TransitionProps?.onEnter}
              onExited={TransitionProps?.onExited}
            >
              <Paper sx={paperSx}>
                <Box sx={popupContentSx}>
                  <List
                    sx={{
                      padding: (theme) => theme.spacing(0, 0, 1, 0),
                    }}
                  >
                    {isLoading || !notificationsData ? (
                      <Box sx={loadingSx}>
                        <CircularProgress size={32} />
                      </Box>
                    ) : (
                      <>
                        <ListSubheader sx={listHeaderSx}>
                          <Typography gutterBottom>
                            <strong>{t("notifications.notifications")}</strong>
                          </Typography>
                          {/* <Button color="secondary" size="small">
                            {t("notifications.markAllAsRead")}
                          </Button> */}
                          {/* <RouteButtonLink
                            size="small"
                            to="/notifications/settings"
                          >
                            {t("notifications.settings")}
                          </RouteButtonLink> */}
                        </ListSubheader>

                        {notificationsData.notifications.length === 0 ? (
                          <ListItem sx={{ justifyContent: "center", py: 2 }}>
                            <Typography
                              sx={{
                                color: "text.secondary",
                              }}
                              variant="body2"
                            >
                              {t("notifications.noUnreadNotifications")}
                            </Typography>
                          </ListItem>
                        ) : (
                          notificationsData.notifications.map(
                            (notification, index) => (
                              <Box
                                key={notification.id}
                                sx={{ position: "relative" }}
                              >
                                {/* Temporary disable React Router DOM link because Rails with React don't correctly handle the link */}
                                {window.reactWithRails ? (
                                  <ListItemButton
                                    component={Link}
                                    data-notification-id={notification.id}
                                    href={notification.url || ""}
                                    onClick={handleClickAway}
                                    onMouseEnter={handleNotificationMouseEnter}
                                    sx={{
                                      alignItems: "flex-start",
                                      display: "flex",
                                      flexDirection: "column",
                                      paddingRight: `${28 + 8}px`,
                                    }}
                                  >
                                    {!notification.seen_at ? (
                                      <Box
                                        sx={{
                                          background: "transparent",
                                          border: 0,
                                          margin: 0,
                                          padding: 0,
                                        }}
                                      >
                                        <Box
                                          sx={{
                                            left: "9px",
                                            position: "absolute",
                                            top: "6px",
                                          }}
                                        >
                                          <Badge
                                            anchorOrigin={{
                                              horizontal: "left",
                                              vertical: "top",
                                            }}
                                            color="primary"
                                            sx={{
                                              zIndex: 0,
                                            }}
                                            variant="dot"
                                          />
                                        </Box>
                                      </Box>
                                    ) : null}
                                    {notification.title ? (
                                      <Box
                                        sx={{
                                          alignItems: "flex-start",
                                          display: "flex",
                                          justifyContent: "space-between",
                                          width: "100%",
                                        }}
                                      >
                                        <Typography gutterBottom>
                                          <b>{notification.title}</b>
                                        </Typography>
                                      </Box>
                                    ) : null}

                                    <Typography
                                      sx={{ color: "text.secondary" }}
                                      variant="caption"
                                    >
                                      {t(
                                        "intlDateTime",
                                        i18nextParseToDate(
                                          notification.created_at,
                                        ),
                                      )}
                                    </Typography>

                                    <Typography
                                      gutterBottom
                                      sx={{ color: "text.secondary" }}
                                      variant="body2"
                                    >
                                      <span
                                        // biome-ignore lint/security/noDangerouslySetInnerHtml: The notification content is sanitized in back-end and safe to use
                                        dangerouslySetInnerHTML={{
                                          __html:
                                            (notification.text || "").length >
                                            150
                                              ? `${(notification.text || "").substring(0, 150)}...`
                                              : notification.text || "",
                                        }}
                                      />
                                    </Typography>
                                  </ListItemButton>
                                ) : (
                                  <ListItemButton
                                    component={LinkRoute}
                                    data-notification-id={notification.id}
                                    onClick={handleClickAway}
                                    onMouseEnter={handleNotificationMouseEnter}
                                    sx={{
                                      alignItems: "flex-start",
                                      display: "flex",
                                      flexDirection: "column",
                                      paddingRight: `${28 + 8}px`,
                                    }}
                                    to={notification.url || ""}
                                  >
                                    {!notification.seen_at ? (
                                      <Box
                                        sx={{
                                          background: "transparent",
                                          border: 0,
                                          margin: 0,
                                          padding: 0,
                                        }}
                                      >
                                        <Box
                                          sx={{
                                            left: "9px",
                                            position: "absolute",
                                            top: "6px",
                                          }}
                                        >
                                          <Badge
                                            anchorOrigin={{
                                              horizontal: "left",
                                              vertical: "top",
                                            }}
                                            color="primary"
                                            sx={{
                                              zIndex: 0,
                                            }}
                                            variant="dot"
                                          />
                                        </Box>
                                      </Box>
                                    ) : null}
                                    {notification.title ? (
                                      <Box
                                        sx={{
                                          alignItems: "flex-start",
                                          display: "flex",
                                          justifyContent: "space-between",
                                          width: "100%",
                                        }}
                                      >
                                        <Typography gutterBottom>
                                          <b>{notification.title}</b>
                                        </Typography>
                                      </Box>
                                    ) : null}

                                    <Typography
                                      sx={{ color: "text.secondary" }}
                                      variant="caption"
                                    >
                                      {t(
                                        "intlDateTime",
                                        i18nextParseToDate(
                                          notification.created_at,
                                        ),
                                      )}
                                    </Typography>

                                    <Typography
                                      gutterBottom
                                      sx={{ color: "text.secondary" }}
                                      variant="body2"
                                    >
                                      <span
                                        // biome-ignore lint/security/noDangerouslySetInnerHtml: The notification content is sanitized in back-end and safe to use
                                        dangerouslySetInnerHTML={{
                                          __html:
                                            (notification.text || "").length >
                                            150
                                              ? `${(notification.text || "").substring(0, 150)}...`
                                              : notification.text || "",
                                        }}
                                      />
                                    </Typography>
                                  </ListItemButton>
                                )}
                                {!notification.read_at ? (
                                  <Box
                                    sx={{
                                      background: "transparent",
                                      border: 0,
                                      margin: 0,
                                      padding: 0,
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        position: "absolute",
                                        right: "8px",
                                        top: "4px",
                                      }}
                                    >
                                      <IconButton
                                        color="default"
                                        data-notification-id={notification.id}
                                        onClick={handleMarkAsRead}
                                        size="small"
                                      >
                                        <Icon icon={mdiClose} />
                                      </IconButton>
                                    </Box>
                                  </Box>
                                ) : null}
                                {index <
                                notificationsData.notifications.length - 1 ? (
                                  <Divider sx={dividerSx} />
                                ) : null}
                              </Box>
                            ),
                          )
                        )}
                        {hasNextPage ? (
                          <>
                            <Divider sx={dividerSx} />
                            <ListItem
                              sx={{
                                alignItems: "center",
                                display: "flex",
                                justifyContent: "center",
                              }}
                            >
                              <Box ref={ref}>
                                <CircularProgress />
                              </Box>
                            </ListItem>
                          </>
                        ) : null}
                      </>
                    )}
                  </List>
                </Box>
                {/* Temporary disable React Router DOM link because Rails with React don't correctly handle the link */}
                {window.reactWithRails ? (
                  <Button
                    component={Link}
                    data-testid="view-all-notifications"
                    fullWidth
                    href="/notifications"
                    onClick={handleClickAway}
                  >
                    <Typography>
                      {t("notifications.viewAll", {
                        count: notificationsData?.notificationsCount,
                      })}
                    </Typography>
                  </Button>
                ) : (
                  <RouteButtonLink
                    data-testid="view-all-notifications"
                    fullWidth
                    onClick={handleClickAway}
                    to="/notifications"
                  >
                    <Typography>
                      {t("notifications.viewAll", {
                        count: notificationsData?.notificationsCount,
                      })}
                    </Typography>
                  </RouteButtonLink>
                )}
              </Paper>
            </Grow>
          </ClickAwayListener>
        )}
      </Popper>
    </>
  );
};
